import React, {FC, useContext} from "react";
import {useTranslate} from "@tolgee/react";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@material-ui/core";
import RemoveCirceIcon from "@material-ui/icons/RemoveCircle";
import RemoveIcon from "@material-ui/icons/Remove";
import AddIcon from "@material-ui/icons/Add";
import {ExtraProduct} from "ts/api/product";
import formatPrice from "tools/formats/formatPrice";
import {CurrencyContext} from "view/context/CurrencyContext";
import {grey} from "@material-ui/core/colors";
import formatExtraProductName from "tools/formats/formatExtraProductName";
import {useQuickOrderId} from "hooks/show/order";
import useStyles from "../../styles";

export type ExtraProductItemProps = {
  product: ExtraProduct;
  onRemoveProduct: (id: string) => void;
  onChangeQuantity: (id: string, quantity: number) => void;
};

const ExtraProductItem: FC<ExtraProductItemProps> = ({
  product,
  onChangeQuantity,
  onRemoveProduct,
}) => {
  const theme = useTheme();
  const {t} = useTranslate();
  const quickOrderId = useQuickOrderId();
  const classes = useStyles();
  const currency = useContext(CurrencyContext);
  const {quantity} = product;
  const productName = `${formatExtraProductName(product.name)}${
    product.isSelectedByDefault ? "*" : ""
  }`;

  return (
    <Box>
      <Box className={classes.product}>
        <Box
          className={classes.productContent}
          style={{flexDirection: "row", alignItems: "center"}}
        >
          <Typography variant="body2" className={classes.productName}>
            {productName}
          </Typography>
          <Box display="flex" alignItems="center">
            <Box display="flex" alignItems="center">
              <IconButton
                aria-label={t("common.aria.decrease")}
                onClick={() => {
                  onChangeQuantity(product.id, quantity - 1);
                }}
                disabled={quantity < 2 || !!quickOrderId}
              >
                <RemoveIcon
                  htmlColor={theme.palette.contrastIcon.main}
                  fontSize="small"
                />
              </IconButton>
              <Box width={16} display="flex" justifyContent="center">
                <Typography>{quantity}</Typography>
              </Box>
              <IconButton
                aria-label={t("common.aria.increase")}
                disabled={!!quickOrderId}
                onClick={() => {
                  onChangeQuantity(product.id, quantity + 1);
                }}
              >
                <AddIcon
                  htmlColor={theme.palette.contrastIcon.main}
                  fontSize="small"
                />
              </IconButton>
            </Box>
            <Box width="80px" textAlign="right">
              <Typography>
                {formatPrice(product.priceWithFees * quantity, currency)}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box className={classes.productRemove}>
          {!product.isSelectedByDefault && (
            <Tooltip title={t("common.shopping_cart.remove_from_cart")}>
              <IconButton
                aria-label={t("common.aria.remove")}
                onClick={() => onRemoveProduct(product.id)}
                disabled={!!quickOrderId}
              >
                <RemoveCirceIcon style={{color: grey[500]}} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ExtraProductItem;
